import { ifDefined } from '#misc/if-defined';

import type { StorageEngine } from './storage-engine';
import type { Deserializer } from './types/deserializer.type';
import type { Serializer } from './types/serializer.type';
import type { SetOptions } from './types/set.options';

/**
 * A typed storage adapter that provides a convenient interface for storing and retrieving values of type `TValue`
 * using an underlying `StorageEngine`. It handles serialization and deserialization of values to and from string
 * representations.
 *
 * @template TValue - The type of the values to be stored and retrieved.
 */
export class TypedStorageAdapter<TValue = unknown> {
  private lastParsed?: TValue | undefined;

  private lastRaw?: unknown;

  constructor(
    private engine: StorageEngine,
    private serialize: Serializer<TValue>,
    private deserialize: Deserializer<TValue>
  ) {}

  /**
   * Clears all entries within the current namespace.
   * - Removes all items from the underlying storage engine that belong to the namespace.
   * - Clears corresponding entries from the in-memory cache if it exists.
   * Posts a message to the storage channel to notify other instances to clear their namespaces as well.
   * @remarks
   * This method ensures that all instances of the storage engine that share the same namespace will clear their
   * storage when this method is called. If the namespace is not defined, the method will not perform any action.
   * Example usage:
   * ```typescript
   * storageAdapter.clearNamespace();
   * ```
   */
  clearNamespace() {
    this.engine.clearNamespace();
  }

  /**
   * Retrieves a value of type `TValue` associated with the given key from the storage engine.
   *
   * @param key - The key for which to retrieve the value.
   * @returns The deserialized value of type `TValue` associated with the key, or `undefined`
   * if the key does not exist or deserialization fails.
   * @remarks
   * This method retrieves the raw string value from the storage engine and then uses the provided deserializer
   * function to convert it into the desired type `TValue`. If the key does not exist in the storage engine, or if
   * deserialization fails (e.g., due to an invalid format), the method will return `undefined`.
   * Example usage:
   * ```typescript
   * const value = storageAdapter.get('myKey');
   * if (value !== undefined) {
   *   // Use the retrieved value of type TValue
   * }
   * ```
   */
  get(key: string): TValue | undefined {
    const raw = this.engine.get(key);

    if (Object.is(raw, this.lastRaw)) {
      return this.lastParsed;
    }

    this.lastRaw = raw;
    this.lastParsed = ifDefined(raw, (definedRaw) => {
      return this.deserialize(String(definedRaw));
    });

    return this.lastParsed;
  }

  /**
   * Removes a value associated with the given key from the storage engine.
   *
   * @param key - The key for which to remove the value.
   * @remarks
   * This method removes the value associated with the given key from the storage engine. If the key does not exist,
   * the method will have no effect.
   * Example usage:
   * ```typescript
   * storageAdapter.remove('myKey');
   * ```
   */
  remove(key: string) {
    this.engine.remove(key);
  }

  /**
   * Stores a value of type `TValue` associated with the given key in the storage engine.
   *
   * @param key - The key for which to store the value.
   * @param value - The value to store, which may be `undefined`.
   * @param options - Optional settings for storing the value.
   * @remarks
   * This method serializes the provided value using the configured serializer and stores it in the
   * underlying storage engine.
   * If the value is `undefined`, it will be stored as such. The optional `options` parameter can be used to customize
   * the storage behavior.
   * Example usage:
   * ```typescript
   * storageAdapter.set('myKey', myValue);
   * ```
   */
  set(key: string, value: TValue | undefined, options?: SetOptions) {
    const raw = this.serialize(value);

    this.engine.set(key, raw, options);
  }

  /**
   * Subscribes to changes in the storage engine.
   *
   * @param listener - A callback function to be invoked when a change occurs.
   * @returns A function to unsubscribe from the changes.
   * @remarks
   * This method allows you to listen for changes in the storage engine. The provided listener will be called
   * whenever a change occurs. To stop listening, call the function returned by this method.
   * Example usage:
   * ```typescript
   * const unsubscribe = storageAdapter.subscribe(() => {
   *   // Handle storage change
   * });
   * // To unsubscribe
   * unsubscribe();
   * ```
   */
  subscribe(listener: () => void) {
    return this.engine.subscribe(listener);
  }
}
